import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import { STORYBOARD_STAGES, ASPECT_RATIOS } from "@/constants/shotSettings";
import { IconSketchBadge, IconConceptBadge, IconPrevisBadge } from "@/components/Icons";

const STAGE_ICON_MAP = { sketch: IconSketchBadge, concept: IconConceptBadge, previs: IconPrevisBadge };

export default function AddShotDialog({ insertAfter, onSubmit, onClose }) {
  const [prompt, setPrompt] = useState("");
  const [shotType, setShotType] = useState("medium");
  const [stage, setStage] = useState("sketch");
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [showPrevisWarning, setShowPrevisWarning] = useState(false);

  const handleSubmit = () => {
    if (!prompt.trim()) return;

    onSubmit({
      prompt: prompt.trim(),
      shot_type: shotType,
      stage,
      aspect_ratio: aspectRatio,
    });
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-[#18181b] border-[#3f3f46] text-[#fafafa] max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-medium">
            {insertAfter
              ? `Add Shot After Shot ${insertAfter.frame_number} (Scene ${insertAfter.scene_number})`
              : "Add New Shot"
            }
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Stage Selection */}
          <div>
            <Label className="text-sm text-[#a1a1aa] mb-2 block">Visual Stage</Label>
            <div className="grid grid-cols-3 gap-2">
              {STORYBOARD_STAGES.map((s) => (
                <button
                  key={s.value}
                  onClick={() => {
                    setStage(s.value);
                    if (s.value === "previs") setShowPrevisWarning(true);
                  }}
                  className={`p-2.5 rounded-lg border text-left transition-all ${
                    stage === s.value
                      ? 'border-[#f97316] bg-[#f97316]/10'
                      : 'border-[#3f3f46] bg-[#27272a] hover:border-[#52525b]'
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    {STAGE_ICON_MAP[s.value] && (() => { const I = STAGE_ICON_MAP[s.value]; return <I size={14} />; })()}
                    <span className="text-xs font-medium text-[#fafafa]">{s.label}</span>
                    {s.value === "previs" && <AlertTriangle size={10} className="text-[#f97316] ml-auto" />}
                  </div>
                  <p className="text-[10px] text-[#71717a] mt-1">{s.desc}</p>
                </button>
              ))}
            </div>
            {stage === "previs" && showPrevisWarning && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="mt-2 p-2.5 rounded-lg bg-[#f97316]/10 border border-[#f97316]/20"
              >
                <div className="flex gap-2">
                  <AlertTriangle size={12} className="text-[#f97316] flex-shrink-0 mt-0.5" />
                  <p className="text-[10px] text-[#a1a1aa] leading-relaxed">
                    {STORYBOARD_STAGES.find(s => s.value === "previs")?.warning}
                  </p>
                </div>
              </motion.div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Shot Type */}
            <div>
              <Label htmlFor="shot-type" className="text-sm text-[#a1a1aa] mb-2 block">
                Shot Type
              </Label>
              <Select value={shotType} onValueChange={setShotType}>
                <SelectTrigger
                  id="shot-type"
                  data-testid="shot-type-select"
                  className="bg-[#27272a] border-transparent text-[#fafafa]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  <SelectItem value="establishing">Establishing Shot</SelectItem>
                  <SelectItem value="wide">Wide Shot</SelectItem>
                  <SelectItem value="medium">Medium Shot</SelectItem>
                  <SelectItem value="close-up">Close-Up</SelectItem>
                  <SelectItem value="extreme-close-up">Extreme Close-Up</SelectItem>
                  <SelectItem value="two-shot">Two Shot</SelectItem>
                  <SelectItem value="over-shoulder">Over the Shoulder</SelectItem>
                  <SelectItem value="pov">POV</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Aspect Ratio */}
            <div>
              <Label htmlFor="aspect-ratio" className="text-sm text-[#a1a1aa] mb-2 block">
                Aspect Ratio
              </Label>
              <Select value={aspectRatio} onValueChange={setAspectRatio}>
                <SelectTrigger
                  id="aspect-ratio"
                  className="bg-[#27272a] border-transparent text-[#fafafa]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  {ASPECT_RATIOS.map((ar) => (
                    <SelectItem key={ar.value} value={ar.value}>
                      {ar.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="shot-prompt" className="text-sm text-[#a1a1aa] mb-2 block">
              Shot Description
            </Label>
            <Textarea
              id="shot-prompt"
              data-testid="shot-prompt-textarea"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe what happens in this shot...\ne.g., 'John walks through the door, looks around nervously'"
              className="bg-[#27272a] border-transparent text-[#fafafa] focus:border-[#f97316] resize-none font-mono text-sm min-h-[100px]"
            />
            <p className="text-xs text-[#71717a] mt-2">
              Tip: Be specific about actions, emotions, camera position, and important visual details
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              data-testid="add-shot-submit-btn"
              onClick={handleSubmit}
              disabled={!prompt.trim()}
              className="flex-1 bg-[#f97316] hover:bg-[#ea580c] text-white disabled:opacity-50"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Add & Generate Shot
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              className="border-[#3f3f46] text-[#a1a1aa] hover:text-[#fafafa] hover:bg-[#27272a]"
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
